import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Grid,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    People as PeopleIcon,
    CheckCircle as CheckCircleIcon,
    AttachMoney as MoneyIcon,
    Event as EventIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import * as http from '../../common/http';
import { APIUrl } from '../../common/apiConfig';

interface QuickStatsData {
    active_students: number;
    today_attendance_pct: number;
    pending_fees: number;
    upcoming_sessions_today: number;
}

const QuickStats: React.FC<{ branchId?: number }> = ({ branchId }) => {
    const [stats, setStats] = useState<QuickStatsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

    const fetchStats = async () => {
        try {
            setLoading(true);
            setError(null);
            
            let url = `${APIUrl}dashboard-coaching/quick-stats`;
            if (branchId) {
                url += `?branch_id=${branchId}`;
            }
            
            const response = await http.get(url);
            if (response && response.status === 'success') {
                setStats(response.result);
                setLastRefresh(new Date());
            } else {
                setError('Failed to fetch quick stats');
            }
        } catch (err) {
            console.error('Error fetching quick stats:', err);
            setError('Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
        
        // Auto-refresh every 5 minutes
        const interval = setInterval(fetchStats, 5 * 60 * 1000);
        
        return () => clearInterval(interval);
    }, [branchId]);

    const StatCard = ({ 
        title, 
        value, 
        icon, 
        color, 
        suffix = '' 
    }: { 
        title: string; 
        value: number; 
        icon: React.ReactNode; 
        color: string; 
        suffix?: string;
    }) => (
        <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {title}
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" color={color}>
                            {value.toLocaleString()}{suffix}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            p: 1,
                            borderRadius: 2,
                            backgroundColor: `${color}15`,
                            color: color,
                        }}
                    >
                        {icon}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );

    if (loading && !stats) {
        return (
            <Card elevation={2}>
                <CardContent>
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card elevation={2}>
                <CardContent>
                    <Alert severity="error">{error}</Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                    Quick Statistics
                </Typography>
                <Tooltip title="Refresh">
                    <IconButton size="small" onClick={fetchStats} disabled={loading}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>
            
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Active Students"
                        value={stats?.active_students || 0}
                        icon={<PeopleIcon />}
                        color="#1976d2"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Today's Attendance"
                        value={stats?.today_attendance_pct || 0}
                        icon={<CheckCircleIcon />}
                        color="#2e7d32"
                        suffix="%"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Pending Fees"
                        value={stats?.pending_fees || 0}
                        icon={<MoneyIcon />}
                        color="#ed6c02"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Upcoming Sessions"
                        value={stats?.upcoming_sessions_today || 0}
                        icon={<EventIcon />}
                        color="#9c27b0"
                    />
                </Grid>
            </Grid>
            
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Last updated: {lastRefresh.toLocaleTimeString()}
            </Typography>
        </Box>
    );
};

export default QuickStats;
